import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MuiTextField from '@mui/material/TextField';
import { AutocompleteProps, fieldToAutocomplete } from 'formik-mui';
import {
    Box,
    Autocomplete,
    AutocompleteRenderInputParams,
    Typography,
} from '@mui/material';
import type { BankAccountType } from '../types/bank.types';
import { BankLogo } from '../../../components/bank-logo/bank-logo';
import { getIn } from 'formik';
import { BulletStatus } from '../../../components/bullet-status/bullet-status';
import useFocusOnError from '../../../components/formik/use-focus-on-error';
import { useAccountsQuery } from '../api/bank.api';

const RenderOption = (props: any, option: BankAccountType) => (
    <Box component="li" {...props} key={option.iban}>
        <BankLogo key={option.iban} bankName={option.bank?.name} />
        <Box sx={{ ml: 1 }}>
            <Typography component="span" sx={{ fontSize: '18' }}>
                {option.status !== 'accepted' && (
                    <Box component="span" sx={{ mr: 1 }}>
                        <BulletStatus type="error" label="غیر فعال" />
                    </Box>
                )}

                <Typography component="span">{option.fullname}</Typography>
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>{option.iban}</Typography>
        </Box>
    </Box>
);

type BankAccountSelectorProps = AutocompleteProps<
    BankAccountType,
    false,
    false,
    false
> & {
    icon?: any;
    mode?: 'self' | 'partner';
    isLoading?: boolean;
    error?: boolean;
    acceptedOnly?: boolean;
    liveValidation?: boolean;
    showValidation?: boolean;
    label?: string;
};

export const BankAccountSelector: React.FC<BankAccountSelectorProps> = (
    props,
) => {
    const {
        acceptedOnly = false,
        liveValidation = true,
        showValidation = true,
        isLoading: externalLoading,
        error: externalError,
        label = 'حساب بانکی',
        icon: _icon,
        mode: _mode,
        ...autocompleteProps
    } = props;

    const [options, updateOptions] = useState<BankAccountType[]>([]);

    const { data, isLoading } = useAccountsQuery(
        {
            perPage: acceptedOnly ? 100 : 15,
            search: acceptedOnly ? { status: 'accepted' } : undefined,
        },
        { skip: !!autocompleteProps?.options },
    );
    const ref = useRef();

    const {
        form: {
            setFieldValue,
            validateField,
            touched,
            errors,
            status,
            submitCount,
            validateOnBlur,
            validateOnChange,
        },
        field: { name, value },
    } = autocompleteProps as any;

    const deferValidationUntilSubmit =
        validateOnBlur === false && validateOnChange === false;
    const fieldTouched = getIn(touched, name);
    const fieldError = getIn(errors, name);
    const fieldStatus = getIn(status, name);
    const canShowValidation =
        showValidation &&
        (Boolean(fieldStatus) ||
            (deferValidationUntilSubmit
                ? submitCount > 0
                : Boolean(fieldTouched)));

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!liveValidation) return;
        if (deferValidationUntilSubmit && submitCount === 0) return;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => validateField(name), 500);
        return () => clearTimeout(timeoutRef.current!);
    }, [
        deferValidationUntilSubmit,
        liveValidation,
        name,
        submitCount,
        validateField,
        value,
    ]);

    useFocusOnError({
        fieldRef: ref,
        name,
    });

    useEffect(() => {
        const incoming: readonly BankAccountType[] =
            autocompleteProps?.options ?? data?.items ?? [];

        updateOptions(
            acceptedOnly
                ? incoming.filter((option) => option.status === 'accepted')
                : [...incoming],
        );
    }, [acceptedOnly, autocompleteProps?.options, data?.items]);

    const onChange: any = useCallback(
        (event: any, option: BankAccountType | null) => {
            setFieldValue(name, option ? option.iban : '', false);
        },
        [setFieldValue, name],
    );

    const selectedOption = useMemo(
        () => options.find((option) => option.iban === value) || null,
        [options, value],
    );

    return (
        <Autocomplete
            {...fieldToAutocomplete(autocompleteProps)}
            options={options}
            onChange={onChange}
            value={selectedOption}
            loading={isLoading || externalLoading}
            isOptionEqualToValue={(option: any, currentValue: any) =>
                option.iban === currentValue?.iban
            }
            getOptionLabel={(option: BankAccountType) => option?.iban ?? ''}
            renderOption={RenderOption}
            renderInput={(params: AutocompleteRenderInputParams) => (
                <MuiTextField
                    {...params}
                    name={name}
                    error={
                        canShowValidation &&
                        Boolean(fieldError || fieldStatus || externalError)
                    }
                    helperText={
                        canShowValidation ? fieldError || fieldStatus : undefined
                    }
                    variant="outlined"
                    label={label}
                    inputRef={ref}
                    sx={{
                        '.MuiInputBase-input': {
                            padding: '16.5px 14px !important',
                        },
                    }}
                />
            )}
            getOptionDisabled={(option) => option.status !== 'accepted'}
        />
    );
};
